import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from '../../dayjsConfig'
import { FormControl, FormHelperText, FormLabel, Grid } from '@material-ui/core'
import { t } from 'ttag'
import YumInput from '../YumInput/YumInput'
import { ErrorMessage } from '../ErrorMessage/ErrorMessage'

type DateField = 'D' | 'M' | 'Y'

interface YumDateOfBirthProps {
  label: string
  helperText?: string
  required?: boolean
  error?: boolean
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  startIcon?: any
  style?: React.CSSProperties
}

// Keep backend-friendly numeric padding while we can show friendlier text in UI.
const pad2 = (value: string) => value.padStart(2, '0')

// Read locale date order so the UI matches the user's culture.
const getLocaleDateOrder = (): DateField[] => {
  try {
    const { timeZone = '', locale = '' } = Intl.DateTimeFormat().resolvedOptions()
    // If they truly look US by both locale and timezone -> MDY.
    if (locale.toLowerCase().startsWith('en-us') && (timeZone.startsWith('America/') || timeZone.startsWith('US/'))) {
      return ['M', 'D', 'Y']
    }
  } catch (error) {
    // Ignore and fall back below.
  }

  // Default to DMY (common outside US).
  return ['D', 'M', 'Y']
}

const YumDateOfBirth = ({
  label,
  helperText,
  required,
  error,
  value,
  onChange,
  startIcon,
  style,
}: YumDateOfBirthProps) => {
  const [day, setDay] = useState('')
  // Store the month input as numeric text while emitting DD.MM.YYYY.
  const [monthInput, setMonthInput] = useState('')
  const [monthNumber, setMonthNumber] = useState('')
  const [year, setYear] = useState('')
  const [focusedField, setFocusedField] = useState<DateField | null>(null)
  // Input refs for auto-advance behavior.
  const dayRef = useRef<HTMLInputElement | null>(null)
  const monthRef = useRef<HTMLInputElement | null>(null)
  const yearRef = useRef<HTMLInputElement | null>(null)

  // Resolve date order on each render so locale changes are reflected.
  const dateOrder = getLocaleDateOrder()
  // We no longer show a year dropdown, so no year list is needed.

  // Resolve the next field based on the locale order.
  const getNextField = useCallback(
    (field: DateField) => {
      const index = dateOrder.indexOf(field)
      if (index === -1 || index >= dateOrder.length - 1) return null
      return dateOrder[index + 1]
    },
    [dateOrder],
  )

  // Focus helper to keep auto-advance logic consistent.
  const focusField = useCallback((field: DateField | null) => {
    if (!field) return
    if (field === 'D') dayRef.current?.focus()
    if (field === 'M') monthRef.current?.focus()
    if (field === 'Y') yearRef.current?.focus()
  }, [])

  // Normalize month input while preserving leading zeros (e.g., "02").
  // Keep out-of-range values so we can show an error instead of auto-correcting.
  const normalizeMonthInput = useCallback((rawInput: string) => {
    const trimmed = rawInput.trim()
    if (!trimmed) {
      return { monthNumber: '', display: '' }
    }
    const numeric = Number(trimmed)
    if (!Number.isNaN(numeric) && Number.isInteger(numeric) && trimmed.length <= 2) {
      return {
        monthNumber: trimmed,
        display: trimmed,
      }
    }
    return { monthNumber: '', display: trimmed }
  }, [])

  const daysInMonth = useMemo(() => {
    // Use a safe fallback year so February still works before year selection.
    if (!monthNumber) return 31
    const yearForCalc = year || '2000'
    return dayjs(`${yearForCalc}-${pad2(monthNumber)}-01`, 'YYYY-MM-DD').daysInMonth()
  }, [monthNumber, year])

  useEffect(() => {
    // Keep internal inputs in sync with the external string value.
    if (focusedField) {
      return
    }
    if (!value) {
      setDay('')
      setMonthInput('')
      setMonthNumber('')
      setYear('')
      return
    }
    const parsed = dayjs(value.trim(), 'DD.MM.YYYY', true)
    if (parsed.isValid()) {
      setDay(parsed.format('DD'))
      const monthValue = parsed.format('MM')
      setMonthNumber(monthValue)
      setMonthInput(monthValue)
      setYear(parsed.format('YYYY'))
      return
    }
    const [nextDay, nextMonth, nextYear] = value.split('.')
    setDay(nextDay || '')
    const normalized = normalizeMonthInput(nextMonth || '')
    setMonthNumber(normalized.monthNumber)
    setMonthInput(normalized.display)
    setYear(nextYear || '')
  }, [value, normalizeMonthInput, focusedField])

  const isDateComplete = day.length > 0 && monthNumber.length === 2 && year.length === 4
  const isInvalidDayForMonth = isDateComplete && parseInt(day, 10) > daysInMonth
  const isInvalidMonth = monthNumber.length === 2 && Number(monthNumber) > 12
  const inputError = error || isInvalidDayForMonth || isInvalidMonth
  // Do not remove user input. Show error only when the date is fully entered.

  const emitPaddedIfComplete = useCallback(
    // Emit padded format only when all fields look complete.
    (nextDay: string, nextMonth: string, nextYear: string) => {
      const dayValue = nextDay.trim()
      const monthValue = nextMonth.trim()
      const yearValue = nextYear.trim()
      const dayIsNumeric = /^\d{1,2}$/.test(dayValue)
      const monthIsNumeric = /^\d{1,2}$/.test(monthValue)
      const yearIsNumeric = /^\d{4}$/.test(yearValue)
      const dayNumber = dayIsNumeric ? Number(dayValue) : 0
      const monthNumberValue = monthIsNumeric ? Number(monthValue) : 0
      const isComplete =
        dayIsNumeric &&
        monthIsNumeric &&
        yearIsNumeric &&
        dayNumber >= 1 &&
        dayNumber <= 31 &&
        monthNumberValue >= 1 &&
        monthNumberValue <= 12

      if (isComplete) {
        const nextValue = `${pad2(dayValue)}.${pad2(monthValue)}.${yearValue}`
        const syntheticEvent = { target: { value: nextValue } } as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
    },
    [onChange],
  )

  const emitRawChange = useCallback(
    (nextDay: string, nextMonth: string, nextYear: string) => {
      // Keep raw input while typing so we do not force padding early.
      const parts = [nextDay.trim(), nextMonth.trim(), nextYear.trim()]
      while (parts.length > 0 && parts[parts.length - 1] === '') {
        parts.pop()
      }
      const nextValue = parts.join('.')
      const syntheticEvent = { target: { value: nextValue } } as React.ChangeEvent<HTMLInputElement>
      onChange(syntheticEvent)
    },
    [onChange],
  )

  const handleFocus = (field: DateField) => () => {
    setFocusedField(field)
  }

  const sanitizeNumericInput = (rawValue: string, maxLength: number) => rawValue.replace(/\D/g, '').slice(0, maxLength)

  const handleBlur = (field: DateField) => () => {
    setFocusedField((current) => (current === field ? null : current))

    if (field === 'Y' && /^\d{2}$/.test(year)) {
      const currentYear = new Date().getFullYear()
      const currentCentury = Math.floor(currentYear / 100) * 100
      const currentTwoDigit = currentYear % 100
      const inputTwoDigit = Number(year)
      // If input is ahead of current year, assume previous century.
      const fullYear =
        inputTwoDigit <= currentTwoDigit ? currentCentury + inputTwoDigit : currentCentury - 100 + inputTwoDigit
      const fullYearString = String(fullYear)
      setYear(fullYearString)
      emitRawChange(day, monthNumber, fullYearString)
      emitPaddedIfComplete(day, monthNumber, fullYearString)
      return
    }

    if (field === 'D' && /^\d$/.test(day)) {
      const paddedDay = pad2(day)
      setDay(paddedDay)
      emitRawChange(paddedDay, monthNumber, year)
      emitPaddedIfComplete(paddedDay, monthNumber, year)
      return
    }

    if (field === 'M' && /^\d$/.test(monthInput)) {
      const paddedMonth = pad2(monthInput)
      setMonthInput(paddedMonth)
      setMonthNumber(paddedMonth)
      emitRawChange(day, paddedMonth, year)
      emitPaddedIfComplete(day, paddedMonth, year)
      return
    }

    // Only pad once all fields are complete to avoid interrupting typing.
    emitPaddedIfComplete(day, monthNumber, year)
  }

  const handleDayChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const nextDay = sanitizeNumericInput(String(event.target.value || ''), 2)
    setDay(nextDay)
    emitRawChange(nextDay, monthNumber, year)
    // Do not auto-advance. Let the user control focus.
  }

  const handleMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const nextInput = sanitizeNumericInput(String(event.target.value || ''), 2)
    const normalized = normalizeMonthInput(nextInput)
    setMonthInput(normalized.display)
    setMonthNumber(normalized.monthNumber)
    emitRawChange(day, normalized.monthNumber, year)
    // Do not auto-advance. Let the user control focus.
  }

  const handleYearChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const nextYear = sanitizeNumericInput(String(event.target.value || ''), 4)
    setYear(nextYear)
    emitRawChange(day, monthNumber, nextYear)
    // Do not auto-advance. Let the user control focus.
  }

  const firstField = dateOrder[0]

  // Keep each date field in a single row even on small screens.
  const dayInput = (
    <Grid item xs={4} sm={4}>
      <YumInput
        label={t`Day`}
        value={day}
        onChange={handleDayChange as any}
        onFocus={handleFocus('D')}
        onBlur={handleBlur('D')}
        error={inputError}
        startIcon={firstField === 'D' ? startIcon : undefined}
        required={required}
        type="text"
        inputProps={{ inputMode: 'numeric', maxLength: 2 }}
        inputRef={dayRef}
        style={{ marginBottom: 0 }}
      />
    </Grid>
  )

  // Keep each date field in a single row even on small screens.
  const monthInputField = (
    <Grid item xs={4} sm={4}>
      <YumInput
        label={t`Month`}
        value={monthInput}
        onChange={handleMonthChange as any}
        onFocus={handleFocus('M')}
        onBlur={handleBlur('M')}
        error={inputError}
        startIcon={firstField === 'M' ? startIcon : undefined}
        required={required}
        type="text"
        inputProps={{ inputMode: 'numeric', maxLength: 2 }}
        inputRef={monthRef}
        style={{ marginBottom: 0 }}
      />
    </Grid>
  )

  // Keep each date field in a single row even on small screens.
  const yearInput = (
    <Grid item xs={4} sm={4}>
      <YumInput
        label={t`Year`}
        value={year}
        onChange={handleYearChange as any}
        onFocus={handleFocus('Y')}
        onBlur={handleBlur('Y')}
        error={inputError}
        startIcon={firstField === 'Y' ? startIcon : undefined}
        required={required}
        type="text"
        inputProps={{ inputMode: 'numeric', maxLength: 4 }}
        inputRef={yearRef}
        style={{ marginBottom: 0 }}
      />
    </Grid>
  )

  // Map locale order to inputs so the UI follows user culture.
  const fieldsMap = { D: dayInput, M: monthInputField, Y: yearInput }

  return (
    <FormControl component="fieldset" style={{ width: '100%', ...style }} error={!!inputError}>
      <FormLabel component="legend" style={{ fontSize: '0.75rem', paddingLeft: '10px' }}>
        {label}
        {/*{required ? ' *' : ''}*/}
      </FormLabel>
      {/* Keep the DOB inputs in one row on mobile. */}
      <Grid container spacing={2} wrap="nowrap" style={{ marginTop: '4px' }}>
        {dateOrder.map((field) => (
          <React.Fragment key={field}>{fieldsMap[field]}</React.Fragment>
        ))}
      </Grid>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {(isInvalidDayForMonth || isInvalidMonth) && <ErrorMessage>{t`Please enter a valid date.`}</ErrorMessage>}
    </FormControl>
  )
}

export default YumDateOfBirth
